import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import moment from "moment";
import emojiRegex from "emoji-regex";
import Animate from "components/animate/animate";
import { Profile_Update, AutoComplete_ZipCode } from "services/Profile";
import { useHistory } from "react-router-dom";
import { AuthConfigContext } from "context/AuthConfigProvider";
import Form from "@rjsf/core"
import { isFestariaAcct } from "utils/Ecommerce"
import { Update_Ecommerce_Profile } from "services/Ecommerce";
import ErrorTop from "components/error/error-top";
import { ProfileDetailsContext } from "context/ProfileDetailsProvider";
import "./style.scss";
import validator from '@rjsf/validator-ajv8';

// CustomTextWidgetをコンポーネント外に定義（再レンダリングによるフォーカス喪失を防止）
const CustomTextWidget = (props) => {
  const { value, onChange, options, ...rest } = props;

  const handleChange = (event) => {
    const inputValue = event.target.value;
    onChange(inputValue || undefined);
  };

  return (
    <div>
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        className={`custom-input-field`}
        {...rest}
      />
    </div>
  );
};

// CustomDateWidgetをコンポーネント外に定義（再レンダリングによるフォーカス喪失を防止）
const CustomDateWidget = ({
  value,
  onChange,
  readonly,
  disabled,
  options,
}) => {
  if (readonly && value) {
    return (
      <input
        type="text"
        value={value || ""}
        readOnly={readonly}
        disabled={disabled}
        className="form-control readonly-date-field"
        onClick={(e) => {
          if (readonly) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      />
    );
  }

  return (
    <input
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      readOnly={readonly}
      disabled={disabled}
      className="form-control"
    />
  );
};

const ProfileEdit = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const authConf = useContext(AuthConfigContext);
  const [schema, setSchema] = useState({});
  const [profileDetails] = useContext(ProfileDetailsContext);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // 初期状態の誕生日を保存（入力中でもreadonlyが変わらないようにする）
  // nullの場合は未判定、空文字列の場合は誕生日なし
  const initialBirthdateRef = React.useRef(null);
  // birthdateのreadonlyが決定されたかどうか
  const birthdateReadonlyDeterminedRef = React.useRef(false);
  // フォームが初期化済みかどうか（入力中のリセット防止）
  const formInitializedRef = React.useRef(false);
  const [uiSchema, setUiSchema] = useState({
    "ui:order": [
      "phone",
      "gender",
      "birthdate",
      "email",
      "*"
    ],
    "email": {
      "ui:placeholder": "example@gmail.com"
    },
    "gender": {
      "ui:widget": "radio"
    },
    "birthdate": {
      "ui:widget": "CustomBirthdateWidget",
      // 初期状態ではfalse（populateFormDataで誕生日の有無を確認後に設定）
      "ui:readonly": false
    },
  });

  const resetAnniversaryValue = () => {
    if (document.getElementById('root_custom_data_anniversary')) {
      document.querySelector('#root_custom_data_anniversary').value = ""
    }
  };

  useEffect(() => {
    const initializeSchema = async () => {
      if (authConf && authConf.configJson && authConf.configJson.form_schema) {
        // スキーマをコピーして、birthdateのif/then条件を削除
        // （if/then条件はreadonlyを強制するが、コードで動的に制御したい）
        const originalSchema = authConf.configJson.form_schema;
        const formSchema = { ...originalSchema };
        // if/then/else条件を削除（birthdateのreadonly制御はuiSchemaで行う）
        delete formSchema.if;
        delete formSchema.then;
        delete formSchema.else;

        // フリガナフィールドにカタカナパターンを追加（create/index.jsと同様）
        // APIスキーマにpatternがない場合でもバリデーションを適用
        if (formSchema.properties) {
          if (formSchema.properties.family_name_furigana) {
            formSchema.properties.family_name_furigana = {
              ...formSchema.properties.family_name_furigana,
              pattern: "^[ァ-ヶー]+$"
            };
          }
          if (formSchema.properties.name_furigana) {
            formSchema.properties.name_furigana = {
              ...formSchema.properties.name_furigana,
              pattern: "^[ァ-ヶー]+$"
            };
          }
        }

        setSchema(formSchema);
        const authUiSchema = authConf.configJson.ui_schema;
        // check if agreement is in form_schema's custom_data & configure ui:help feature
        if (formSchema.properties.custom_data && formSchema.properties.custom_data.properties.agreement) {
          if (authUiSchema) {
            const agreementUi = {
              "ui:help": (
                <div>
                  {authConf && authConf.configJson && authConf.configJson.pdf_agreement ?
                    (
                      <>
                        <a target="_blank" rel="noopener noreferrer" href={authConf.configJson.pdf_agreement}>
                          {t("profile_page.edit_page.lbl_terms")}
                        </a>
                      </>
                    ) :
                    (
                      <></>
                    )
                  }
                  {(authConf || authConf.configJson) && !authConf.configJson.pdf_agreement ?
                    (
                      <>
                        <a target="_blank" rel="noopener noreferrer" href="/agreement">
                          利用規約はこちら
                        </a>
                      </>
                    ) :
                    (
                      <></>
                    )
                  }
                </div>
              )
            };
            const anniversaryUi = {
              "ui:help": (
                <div>
                  <div>
                    <>
                      <button class="form-control" type="button" style={{ width: '180px', marginTop: '10px' }} onClick={() => resetAnniversaryValue()}>結婚記念日リセット</button>
                    </>
                  </div>
                </div>
              )
            };
            let combinedUiSchema = {
              ...authUiSchema
            };
            combinedUiSchema.custom_data = {
              ...authUiSchema.custom_data,
              agreement: agreementUi,
              anniversary: anniversaryUi
            };
            setUiSchema(prevState => {
              // birthdateのui:readonlyはpopulateFormDataで設定された値を優先
              // birthdateReadonlyDeterminedRefがtrueの場合のみ、initialBirthdateRefの値を使用
              let birthdateReadonly = prevState.birthdate ? prevState.birthdate['ui:readonly'] : false;
              if (birthdateReadonlyDeterminedRef.current) {
                birthdateReadonly = initialBirthdateRef.current !== '';
              }

              return {
                ...prevState,
                ...combinedUiSchema,
                birthdate: {
                  ...(combinedUiSchema.birthdate || {}),
                  "ui:widget": "CustomBirthdateWidget",
                  "ui:readonly": birthdateReadonly
                },
                family_name: { ...(combinedUiSchema.family_name || {}), "ui:widget": "CustomTextWidget" },
                first_name: { ...(combinedUiSchema.first_name || {}), "ui:widget": "CustomTextWidget" },
                family_name_furigana: { ...(combinedUiSchema.family_name_furigana || {}), "ui:widget": "CustomTextWidget" },
                name_furigana: { ...(combinedUiSchema.name_furigana || {}), "ui:widget": "CustomTextWidget" },
              };
            });
          }
          else if (!authUiSchema) {
            const agreementUi = {
              "custom_data": {
                "agreement": {
                  "ui:help": (
                    <div>
                      {authConf && authConf.configJson && authConf.configJson.pdf_agreement ?
                        (
                          <>
                            <a target="_blank" rel="noopener noreferrer" href={authConf.configJson.pdf_agreement}>
                              {t("profile_page.edit_page.lbl_terms")}
                            </a>
                          </>
                        ) :
                        (
                          <></>
                        )
                      }
                    </div>
                  )
                }
              }
            };
            const anniversaryUi = {
              "custom_data": {
                "anniversary": {
                  "ui:help": (
                    <div>
                      <div>
                        <>
                          <button class="form-control" type="button" style={{ width: '180px', marginTop: '10px' }} onClick={() => resetAnniversaryValue()}>結婚記念日リセット</button>
                        </>
                      </div>
                    </div>
                  )
                }
              }
            };
            setUiSchema(prevState => ({ ...prevState, ...agreementUi, ...anniversaryUi }));
          }
        }
        else if (authUiSchema) {
          setUiSchema(prevState => {
            // birthdateのui:readonlyはpopulateFormDataで設定された値を優先
            let birthdateReadonly = prevState.birthdate ? prevState.birthdate['ui:readonly'] : false;
            if (birthdateReadonlyDeterminedRef.current) {
              birthdateReadonly = initialBirthdateRef.current !== '';
            }

            return {
              ...prevState,
              ...authUiSchema,
              birthdate: {
                ...(authUiSchema.birthdate || {}),
                "ui:widget": "CustomBirthdateWidget",
                "ui:readonly": birthdateReadonly
              }
            };
          });
        }
      }
    };
    initializeSchema();
  }, [authConf, t]);

  useEffect(() => {
    const populateFormData = () => {
      const profileIsEmpty = Object.keys(profileDetails).length === 0;
      // 既にフォームが初期化済みの場合はスキップ（入力中のリセット防止）
      if (formInitializedRef.current) {
        return;
      }
      if (!profileIsEmpty) {
        for (let item in profileDetails) {
          if (profileDetails && profileDetails.custom_data && profileDetails.hasOwnProperty(item) && profileDetails.custom_data.hasOwnProperty(item)) {
            delete profileDetails[item]
          }
          if (profileDetails[item] === null) {
            profileDetails[item] = ""
          }
          // empty name if login with social
          if (item === "name" && profileDetails.identities && !profileDetails.identities.filter((i) => i.provider === "festaria").length) {
            profileDetails[item] = ""
          }
          if (item === "custom_data") {
            for (let custItem in profileDetails.custom_data) {
              // avoid date format errors with "anniversary" option (RRR client) -> set anniversary to undefined rather than ""
              if (custItem === "anniversary" && (profileDetails.custom_data[custItem] === "" || profileDetails.custom_data[custItem] === null)) {
                profileDetails.custom_data[custItem] = undefined;
              }
              if (profileDetails.custom_data[custItem] === null) {
                // for agreement boolean
                if (custItem === "agreement") {
                  profileDetails.custom_data[custItem] = false
                }
                if (custItem === "karen_flower") {
                  profileDetails.custom_data[custItem] = []
                }
                else {
                  profileDetails.custom_data[custItem] = ""
                }
              }
            }
          }
        }

        if (profileDetails.custom_data) {
          const ms = profileDetails.custom_data.marital_status;
          if (!['未婚', '既婚', '選択なし'].includes(ms)) {
            profileDetails.custom_data.marital_status = '選択なし';
          }
        }

        // 初回のみ初期状態の誕生日を保存
        if (initialBirthdateRef.current === null) {
          initialBirthdateRef.current = profileDetails.birthdate || '';
          // readonly判定が完了したことをマーク
          birthdateReadonlyDeterminedRef.current = true;

          // 誕生日のreadonly設定を初期状態のみで判定（入力中に変わらないようにする）
          const hasBirthdate = initialBirthdateRef.current && initialBirthdateRef.current !== '';
          setUiSchema(prevState => ({
            ...prevState,
            "birthdate": {
              "ui:widget": "CustomBirthdateWidget",
              "ui:readonly": hasBirthdate  // 初期状態で誕生日が未記入の場合はfalse、記入済みの場合はtrue
            }
          }));
        }

        if (profileDetails.birthdate) {
          const convertedBirthDay = createBirthDateString(profileDetails.birthdate);
          setFormData({ ...profileDetails, birthdate: convertedBirthDay })
        } else {
          profileDetails.birthdate = null;
          setFormData(profileDetails)
        }
        // フォーム初期化完了をマーク
        formInitializedRef.current = true;
      }
    };
    populateFormData();
  }, [schema, profileDetails])


  const createBirthDateString = (day) => {
    let bdate = new Date(day)
    const offset = bdate.getTimezoneOffset();
    bdate = new Date(bdate.getTime() - (offset * 60 * 1000));
    return bdate.toISOString().split('T')[0].replaceAll("-", "");
  }

  const submit = async (values) => {
    let dataVals = values.formData;
    let formatProfileData = {};

    if (dataVals.birthdate) {
      formatProfileData = {
        ...dataVals,
        birthdate: new Date(dataVals.birthdate).toISOString(),
        birth_day: dataVals.birthdate.slice(8, 10),
        birth_month: dataVals.birthdate.slice(5, 7),
        birth_year: dataVals.birthdate.slice(0, 4),
      };
    } else {
      formatProfileData = {
        ...dataVals
      }
    }
    // if a custom_data item is undefined, give it an empty string. This prevents duplicate data in profile info from showing in the absence of a custom_data value
    if (formatProfileData && formatProfileData.custom_data) {
      for (let item in formatProfileData.custom_data) {
        if (formatProfileData.custom_data[item] === undefined) {
          formatProfileData.custom_data[item] = ""
        }
      }
    }


    if (document.getElementById('root_custom_data_anniversary')) {
      if (document.querySelector('#root_custom_data_anniversary').value === "") {
        formatProfileData.custom_data['anniversary'] = ""
      }
    }
    // let name = "";
    // if(formatProfileData.first_name && formatProfileData.last_name) {
    //   name = `${formatProfileData.first_name} ${formatProfileData.last_name}`;
    //   formatProfileData.name = name;
    // } else if(formatProfileData.first_name && !formatProfileData.last_name) {
    //   name = formatProfileData.first_name;
    //   formatProfileData.name = name;
    // } else {
    //   name = formatProfileData.name;
    //   formatProfileData.name = name;
    // }

    // console.log('formatProfileData: ', formatProfileData);
    // return
    const token = localStorage.getItem("accesstoken");
    const submit = await Profile_Update(formatProfileData, token);
    if (submit) {
      console.log(submit);
      localStorage.setItem("userinfo", JSON.stringify(submit.data))
      Swal.fire({
        icon: "success",
        title: t("profile_page.edit_page.update_success_title"),
        showConfirmButton: false,
        allowOutsideClick: false,
      });
      setTimeout(() => {
        Swal.close();
        window.location.href = "/profile";
      }, 3000);
    }
  }

  const festariaSubmit = async (values) => {
    let formValues = values.formData;

    setFormData(formValues);
    if (formValues?.custom_data?.zipcode) {
      formValues.custom_data.zipcode = formValues.custom_data.zipcode.toString();
    }
    // if user didn't input an anniversary date, this changes anniversary from undefined to empty string
    if (formValues?.custom_data) {
      for (let item in formValues.custom_data) {
        if (formValues.custom_data[item] === undefined) {
          formValues.custom_data[item] = ""
        }
      }
    }

    if (document.getElementById('root_custom_data_anniversary')) {
      if (document.querySelector('#root_custom_data_anniversary').value === "") {
        formValues.custom_data['anniversary'] = ""
      }
    }
    // console.log(formValues)
    // return
    setLoading(true);
    const token = localStorage.getItem("accesstoken") || "";
    const updateFestariaProfile = await Update_Ecommerce_Profile(formValues, token);

    if (updateFestariaProfile?.message === "Success") {
      // console.log('2. updateFestariaProfile success:', updateFestariaProfile);
      Swal.fire({
        icon: "success",
        title: t("profile_page.create_page.edit_success"),
        showConfirmButton: false,
        allowOutsideClick: false,
      });
      setTimeout(() => {
        setLoading(false);
        Swal.close();
        window.location.href = "/home";
      }, 1500);
    } else {
      setLoading(false);
      setError(true);
      // APIエラーレスポンス形式に対応:
      // 1. { errors: "エラーメッセージ" } - 文字列の場合
      // 2. { errors: { error_description: "..." } } - オブジェクトの場合（レガシー互換）
      // 3. { message: "エラーメッセージ" } - messageフィールドの場合
      const responseData = updateFestariaProfile?.response?.data;
      let errorDescription = null;

      if (responseData?.errors) {
        // errorsが文字列の場合（現在のAPI形式）
        if (typeof responseData.errors === 'string') {
          errorDescription = responseData.errors;
        }
        // errorsがオブジェクトでerror_descriptionを持つ場合（レガシー互換）
        else if (responseData.errors?.error_description) {
          errorDescription = responseData.errors.error_description;
        }
      }
      // errorsがない場合はmessageを使用（"Failed"以外）
      if (!errorDescription && responseData?.message && responseData.message !== "Failed") {
        errorDescription = responseData.message;
      }

      if (errorDescription) {
        setErrorMessage(errorDescription)
      } else {
        setErrorMessage("会員情報を更新できませんでした");
      }
    }
  }

  useEffect(() => {
    // Reset error message after 7 seconds
    let festariaErrorDisplay = setTimeout(() => {
      setError(false)
      setErrorMessage('')
    }, 7000);

    return () => clearTimeout(festariaErrorDisplay)
  }, [error, errorMessage])

  // function validateEmails(formData, errors) {
  //   if ((formData.email !== formData.custom_data.emailConfirm) || !formData.custom_data.emailConfirm) {
  //     errors.custom_data.emailConfirm.addError("Emails don't match");
  //   }
  //   return errors;
  // }

  const customFormats = {
    'phone-jp': /^[0-9]*$/
  };

  // 絵文字が含まれているかをチェック（emoji-regexライブラリ使用 - Unicode標準準拠）
  const containsEmoji = (value) => {
    if (typeof value !== 'string') return false;
    const regex = emojiRegex();
    return regex.test(value);
  };

  // 絵文字エラーがあるフィールドを追跡（transformErrorsでパターンエラーを抑制するため）
  // useRefを使用してState更新による再レンダリングを防ぐ
  const emojiErrorFieldsRef = React.useRef(new Set());

  // Custom validation function for React JSON Schema Form
  const customValidate = (formData, errors) => {
    // Validate birthdate only if it's being edited (not readonly)
    if (formData.birthdate && !uiSchema.birthdate['ui:readonly']) {
      const birthdateError = validateDate(formData.birthdate);
      if (birthdateError) {
        errors.birthdate.addError(birthdateError);
      }
    }

    // 絵文字バリデーション（姓・名・カタカナフィールド）
    // 絵文字エラーを優先し、パターンエラーは表示しない
    emojiErrorFieldsRef.current = new Set();

    if (formData.family_name && containsEmoji(formData.family_name)) {
      errors.family_name.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.family_name');
    }
    if (formData.first_name && containsEmoji(formData.first_name)) {
      errors.first_name.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.first_name');
    }
    if (formData.family_name_furigana && containsEmoji(formData.family_name_furigana)) {
      errors.family_name_furigana.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.family_name_furigana');
    }
    if (formData.name_furigana && containsEmoji(formData.name_furigana)) {
      errors.name_furigana.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.name_furigana');
    }

    return errors;
  };

  // Custom date validation function using Moment.js
  const validateDate = (dateString) => {
    // 空チェックはスキーマのrequiredバリデーションに任せる
    if (!dateString || dateString.trim() === '') {
      return null;
    }

    // Check if date format is exactly YYYYMMDD (8 digits)
    const dateRegex = /^\d{8}$/;
    if (!dateRegex.test(dateString)) {
      return "日付はYYYYMMDD形式（8桁の数字）で入力してください。";
    }

    // Parse YYYYMMDD format using Moment.js
    const momentDate = moment(dateString, 'YYYYMMDD', true); // strict parsing

    // Check if date is valid
    if (!momentDate.isValid()) {
      return "有効な日付を入力してください。";
    }

    // Check if date is in the future
    if (momentDate.isAfter(moment(), 'day')) {
      return "未来の日付は入力できません。";
    }

    // Check if date is too far in the past (reasonable age limit - 150 years)
    const minDate = moment().subtract(150, 'years');
    if (momentDate.isBefore(minDate, 'day')) {
      return "有効な生年月日を入力してください。";
    }

    return null; // Valid date
  };

  function transformErrors(errors) {
    var errorMessage = [];
    var existErrors = [];

    // 絵文字エラーがあるフィールドを特定（フィールド名のみを抽出）
    // propertyの形式が ".name_furigana" または "name_furigana" などの可能性があるため、
    // フィールド名の末尾部分でマッチングする
    var emojiErrorFields = new Set();
    errors.forEach(error => {
      if (error.message === '使用できない文字（絵文字・記号など）が含まれています。') {
        // propertyからフィールド名を抽出 (例: ".name_furigana" -> "name_furigana")
        var fieldName = error.property ? error.property.replace(/^\./, '') : '';
        if (fieldName) {
          emojiErrorFields.add(fieldName);
        }
        if (!existErrors.includes(error.property)) {
          existErrors.push(error.property);
          errorMessage.push(error);
        }
      }
    });

    if (document.getElementById('root_custom_data_zipcode')) {
      var root_zipcode = document.querySelector('#root_custom_data_zipcode').value;
      if (root_zipcode.trim().length !== 7) {
        var zipcode_error = {
          "name": "format",
          "property": ".custom_data.zipcode",
          "message": "7桁で半角数字のみを入力してください。",
          "params": {
            "format": "phone-jp"
          },
          "stack": ".zipcode should match format \"phone-jp\"",
          "schemaPath": "#/properties/zipcode/format"
        }

        errors.push(zipcode_error)
      }
    }

    if (document.getElementById('root_email')) {
      var root_email = document.querySelector('#root_email').value;
      const emailRegex = /\.(cn|us|uk|de|fr|in|ca|au|br|ru|jp|kr|it|es|nl|se|mx|ch|ar|com|org|net|edu|gov|info|biz|name|pro|tv|int|xyz|shop|tech|app|blog|news|music|store|club|co|online|me|design|mil|coop|aero|museum|asia|eu|lat|africa|sg|team|tw|io|vn|re|cloud|md|ph)$/;
      const isValidEmail = emailRegex.test(root_email);
      if (!isValidEmail) {
        var email_error = {
          "name": "format",
          "property": ".email",
          "message": "有効なメールアドレスを入力してください。",
          "params": {
            "format": "email"
          },
          "stack": ".email should match format email",
          "schemaPath": "#/properties/email/format"
        }

        errors.push(email_error)
      }
    }
    errors.some(error => {
      // console.log(error)
      if (error.property === ".custom_data.occupation" || error.property === ".custom_data.anniversary") {
        return false;
      }
      if (error.name === "enum") {
        if (error.property === ".custom_data.agreement") {
          error.message = "利用規約の同意が必要になります"
          errorMessage.push(error)
        } else if (error.property === ".gender") {
          if (existErrors.includes(".gender")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
        } else if (error.property === ".custom_data.favorite_store") {
          if (existErrors.includes(".custom_data.favorite_store")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
        } else if (error.property === ".custom_data.prefCode") {
          if (existErrors.includes(".custom_data.prefCode")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
        } else {
          existErrors.push(error.property)
          error.message = "必須項目を入力してください。";
          errorMessage.push(error)
        }
      }
      // if(error.property === ".family_name" || error.property === ".email") {
      //   error.message= "必須項目を入力してください。"
      //   errorMessage.push(error)
      // }
      console.log(error)
      if (error.name === "required" && error.property !== '.custom_data.zipcode') {
        error.message = "必須項目を入力してください。";
        errorMessage.push(error)
        existErrors.push(error.property)
      }
      if (error.name === "type") {
        if (error.property === '.custom_data.favorite_store' || error.property === '.custom_data.prefCode' || error.property === '.gender') {
          return false;
        }
        if (error.params.type === "string" && !existErrors.includes(error.property)) {
          error.message = "必須項目を入力してください。";
          errorMessage.push(error)
          existErrors.push(error.property)
        }
      }
      if (error.name === "maxLength") {
        if (error.property === ".password") {
          if (existErrors.includes(".password")) {
            return false;
          }
          existErrors.push(error.property)
          // error.message = `8文字以上${error.params.limit || 16}文字以下を入力してください`
          // error.message = '半角英数字記号の8～16文字で入力してください。'
          error.message = '8～16文字で半角英数字記号のみを入力してください。「"」「,」「:」「<」「>」「[」「]」「`」は利用できません。'
          errorMessage.push(error)
        } else if (error.property === ".phone") {
          if (existErrors.includes(".phone")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '10〜11桁で半角数字のみを入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".email") {
          if (existErrors.includes(".email")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '45文字以内で半角英数記号のみを入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".custom_data.city") {
          if (existErrors.includes(".custom_data.city")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '2〜45文字で入力してください。'
          errorMessage.push(error)
        } else {
          error.message = `${error.params.limit}文字以内で入力してください。`
          errorMessage.push(error)
        }
      }
      if (error.name === "minLength" || error.name === "pattern") {
        if (error.property === ".family_name_furigana" && error.name === "pattern") {
          // 絵文字エラーがある場合はパターンエラーをスキップ（絵文字エラーを優先）
          // DOMから直接値を取得して絵文字チェック（customValidateのエラーはtransformErrorsに渡されないため）
          var familyNameFuriganaInput = document.getElementById('root_family_name_furigana');
          var familyNameFuriganaValue = familyNameFuriganaInput ? familyNameFuriganaInput.value : '';
          if (existErrors.includes(".family_name_furigana") || containsEmoji(familyNameFuriganaValue)) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '全角カタカナのみで入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".name_furigana" && error.name === "pattern") {
          // 絵文字エラーがある場合はパターンエラーをスキップ（絵文字エラーを優先）
          // DOMから直接値を取得して絵文字チェック（customValidateのエラーはtransformErrorsに渡されないため）
          var nameFuriganaInput = document.getElementById('root_name_furigana');
          var nameFuriganaValue = nameFuriganaInput ? nameFuriganaInput.value : '';
          if (existErrors.includes(".name_furigana") || containsEmoji(nameFuriganaValue)) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '全角カタカナのみで入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".password") {
          if (existErrors.includes(".password")) {
            return false;
          }
          existErrors.push(error.property)
          // error.message = `半角英数字${error.params.limit || 8}文字以上で入力してください`
          error.message = '8～16文字で半角英数字記号のみを入力してください。「"」「,」「:」「<」「>」「[」「]」は利用できません。'
          errorMessage.push(error)
        } else if (error.property === ".phone") {
          if (existErrors.includes(".phone")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '10〜11桁で半角数字のみを入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".birthdate") {
          if (existErrors.includes(".birthdate")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '8桁で半角数字のみを入力してください（例: 19900101）。'
          errorMessage.push(error)
        } else if (error.property === ".custom_data.city") {
          if (existErrors.includes(".custom_data.city")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '2〜45文字で入力してください。'
          errorMessage.push(error)
        } else if (error.name === "minLength" && error.params.limit === 1) {
          // 名前フィールドのminLength: 1は必須項目として扱う
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
          existErrors.push(error.property)
        } else {
          error.message = error.params.limit ? `${error.params.limit}文字以下ではありません` : `入力形式が正しくありません`
          errorMessage.push(error)
        }
      }
      if (error.name === "format") {
        if (error.params.format === "phone-jp" && error.property !== ".custom_data.zipcode") {
          if (existErrors.includes(".phone")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "10〜11桁で半角数字のみを入力してください。"
          errorMessage.push(error)
        } else if (error.params.format === "email") {
          if (existErrors.includes(".email")) {
            return false;
          }
          existErrors.push(".email")
          error.message = "有効なメールアドレスを入力してください。"
          errorMessage.push(error)
        }
      }
      if (error.property === ".custom_data.zipcode") {
        if (existErrors.includes(".custom_data.zipcode")) {
          return false;
        }
        existErrors.push(error.property)
        error.message = `7桁で半角数字のみを入力してください。`
        errorMessage.push(error)
      }
      // if(error.message === "should match some schema in anyOf") {
      //   if(error.property === ".gender") {
      //     error.message = "必須項目を入力してください。"
      //     errorMessage.push(error)
      //   } else {
      //     error.message = "";
      //   }
      // } 
      if (error.message === "should be boolean") {
        error.message = "";
      }
      // 絵文字エラーは既に先頭で処理済みなのでスキップ
      if (error.message === '使用できない文字（絵文字・記号など）が含まれています。') {
        return false;
      }
      // return error;
      return false;
    });

    return errorMessage;
    // return errors.map(error => {
    //   if (error.name === "enum") {
    //     if (error.property === ".custom_data.agreement") {
    //       error.message = "利用規約の同意が必要になります"
    //     } else if (error.property === ".gender") {
    //       error.message = ""
    //     } else {
    //       error.message = "こちらは必須項目です";
    //     }
    //   }
    //   if (error.name === "required") {
    //     error.message = "こちらは必須項目です";
    //   }
    //   if (error.name === "maxLength") {
    //     if(error.property === ".password") {
    //       error.message = `8文字以上${error.params.limit}文字以下を入力してください`
    //     } else {
    //       error.message = `${error.params.limit}文字以上ではありません`
    //     }
    //   }
    //   if (error.name === "minLength") {
    //     if (error.property === ".password") {
    //       error.message = `${error.params.limit}文字以上16文字以下を入力してください`
    //     } else {
    //       error.message = `${error.params.limit}文字以下ではありません`
    //     }
    //   }
    //   if (error.name === "format") {
    //     if (error.params.format === "phone-jp") {
    //       error.message = "数字だけでお願いします"
    //     } else if (error.params.format === "email") {
    //       error.message = "有効なEメールを使用してください"
    //     } else {
    //       error.message = `フォーマットは${error.params.format}と一致する必要があります`
    //     }
    //   }
    //   if (error.message === "should match some schema in anyOf") {
    //     if (error.property === ".gender") {
    //       error.message = "こちらは必須項目です"
    //     } else {
    //       error.message = "";
    //     }
    //   }
    //   if (error.message === "should be boolean") {
    //     error.message = "";
    //   }
    //   return error;
    // });
  };

  function onError(errors) {
    const errorField = document.querySelector('.field-error');
    if (errorField) errorField.scrollIntoView();
  }


  // Custom birthdate widget with 8-digit limit - memoized to prevent re-creation
  const CustomBirthdateWidget = React.useCallback(({
    value,
    onChange,
    disabled,
    readonly,
  }) => {
    const handleChange = (e) => {
      const inputValue = e.target.value;
      // Only allow digits and limit to 8 characters
      const digitsOnly = inputValue.replace(/[^0-9]/g, '');
      if (digitsOnly.length <= 8) {
        onChange(digitsOnly);
      }
    };

    // 明示的にtrue以外はreadonly=falseとして扱う
    // RJSFはundefinedや空文字列を渡すことがあるため
    const isReadonly = readonly === true;

    // readOnly属性は明示的にtrueの場合のみ追加（falseの場合は属性自体を追加しない）
    const readOnlyProps = isReadonly ? { readOnly: true } : {};

    return (
      <input
        type="text"
        value={value || ""}
        onChange={handleChange}
        disabled={disabled}
        {...readOnlyProps}
        className="form-control"
        placeholder="YYYYMMDD"
        maxLength={8}
        style={isReadonly ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
      />
    );
  }, []);

  return (
    <Animate>
      <div className="profile-edit">
        <div className="card">
          {error && <ErrorTop message={errorMessage} />}
          <div className="card-title">
            <h1>{t("profile_page.edit_page.title")}</h1>
            <div className="title-bar"></div>
          </div>
          <div className="card-body">
            {schema && Object.keys(schema).length > 0 ? (
              <Form
                noHtml5Validate
                validate={customValidate}
                transformErrors={transformErrors}
                onError={onError}
                customFormats={customFormats}
                schema={schema}
                uiSchema={uiSchema}
                formData={formData}
                onSubmit={values => {
                  isFestariaAcct(authConf?.configJson?.account)
                    ? festariaSubmit(values)
                    : submit(values)
                }}
                onChange={AutoComplete_ZipCode}
                validator={validator}
                widgets={{
                  CustomTextWidget: CustomTextWidget,
                  CustomBirthdateWidget: CustomBirthdateWidget,
                  CustomDateWidget: CustomDateWidget // 关键：这个名字要跟 JSON 里的一样
                }}
              >
                <div className="action-area">
                  <div className="back">
                    <button type="button" className="btn btn-back" onClick={() => history.goBack()}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                  </div>
                  <div className="submission">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-save"
                    >
                      {t("profile_page.edit_page.button_submit")}
                    </button>
                  </div>
                </div>
              </Form>
            ) : (
              <>
                <div className="no-schema">
                  <span>{t("common.no_info")}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Animate>
  );
};

export default ProfileEdit;
